(() => {
  if (customElements.get('bl-quick-add')) return;
  const state = { cart: null, busy: false, uncertain: false, readPromise: null, components: new Set() };
  const integer = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) ? Number(value) : null;
  const sum = (cart, id, scope) => cart.items.reduce((total, item) => total + (String(item[scope === 'product' ? 'product_id' : 'variant_id']) === String(id) ? Number(item.quantity) : 0), 0);
  const text = (component, key, replacements = {}) => Object.entries(replacements).reduce((value, [name, replacement]) => value.replaceAll(`[${name}]`, String(replacement)), component.config.strings[key] || key);
  const refreshAll = () => state.components.forEach(component => component.refresh());
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const result = await response.json();
      return { response, result };
    } finally { clearTimeout(timer); }
  };
  const fetchCart = async config => {
    const { response, result: cart } = await request(`${config.cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart-read');
    if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count)) throw new Error('cart-data');
    state.cart = cart;
    state.uncertain = false;
    return cart;
  };
  const initialCart = config => {
    if (!state.readPromise) state.readPromise = fetchCart(config).finally(() => { state.readPromise = null; });
    refreshAll();
    return state.readPromise;
  };
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { state.components.add(this); this.refresh(); return; }
      this.initialized = true;
      this.catalog = this.closest('.bl-catalog');
      const configElement = this.catalog?.querySelector('[data-bl-quick-add-config]');
      if (!configElement) return;
      try { this.config = JSON.parse(configElement.textContent); } catch { return; }
      this.form = this.querySelector('form');
      this.select = this.querySelector('select');
      this.quantity = this.querySelector('[name="quantity"]');
      this.button = this.querySelector('[type="submit"]');
      this.message = this.querySelector('.bl-quick-add__message');
      this.retry = this.querySelector('.bl-quick-add__retry');
      this.notice = this.catalog.querySelector('[data-bl-quick-add-notice]');
      const card = this.closest('li');
      this.cardFormat = card?.querySelector('[data-bl-card-format]');
      this.cardPrice = card?.querySelector('[data-bl-card-price]');
      this.originalFormat = this.cardFormat?.textContent || '';
      this.originalPrice = this.cardPrice?.textContent || '';
      this.form.addEventListener('submit', event => this.submit(event));
      this.button.addEventListener('click', event => {
        if (this.selection() || this.button.disabled) return;
        event.preventDefault();
        this.select.focus();
        try { this.select.showPicker?.(); } catch {}
      });
      this.select.addEventListener('change', () => { this.message.textContent = ''; this.quantity.value = '1'; this.syncMetadata(); this.refresh(); });
      this.retry.addEventListener('click', () => this.recheck());
      state.components.add(this);
      this.syncMetadata();
      this.refresh();
      if (!state.cart) {
        this.message.textContent = text(this, 'checking');
        initialCart(this.config).then(() => { this.message.textContent = ''; refreshAll(); }).catch(() => { this.message.textContent = text(this, 'check_failed'); this.refresh(); });
      }
    }
    disconnectedCallback() { state.components.delete(this); }
    syncMetadata() {
      const option = this.select.selectedOptions[0];
      const selected = !!option?.value;
      if (this.cardFormat) {
        const format = selected ? option.dataset.format ?? this.originalFormat : this.originalFormat;
        this.cardFormat.textContent = format;
        this.cardFormat.hidden = !format;
      }
      if (this.cardPrice) this.cardPrice.textContent = selected ? option.dataset.price || this.originalPrice : this.originalPrice;
    }
    selection() {
      const option = this.select.selectedOptions[0];
      if (!option?.value) return null;
      const limit = integer(option.dataset.limit);
      const purchased = integer(option.dataset.purchased);
      return { id: option.value, available: option.dataset.available === 'true', limit, purchased, capped: option.dataset.capEnabled !== 'false', stockTracked: option.dataset.stockTracked === 'true', stock: integer(option.dataset.stock) };
    }
    purchaseRemaining(selection, cart = state.cart) {
      if (!selection || !cart) return 0;
      if (!selection.capped) return Infinity;
      if (selection.limit === null || selection.purchased === null) return 0;
      const scope = this.dataset.limitScope;
      const id = scope === 'product' ? this.dataset.productId : selection.id;
      return Math.max(0, selection.limit - selection.purchased - sum(cart, id, scope));
    }
    remaining(selection, cart = state.cart) {
      if (!selection || !cart) return 0;
      const purchaseRemaining = this.purchaseRemaining(selection, cart);
      if (!selection.stockTracked) return purchaseRemaining;
      if (selection.stock === null) return 0;
      return Math.min(purchaseRemaining, Math.max(0, selection.stock - sum(cart, selection.id, 'variant')));
    }
    refresh() {
      const selected = this.selection();
      const blocked = state.busy || !state.cart || state.uncertain;
      const remaining = this.remaining(selected);
      this.select.disabled = blocked;
      this.quantity.disabled = blocked || !selected || !selected.available || remaining < 1;
      this.button.disabled = blocked || (!!selected && (!selected.available || remaining < 1));
      this.button.type = selected ? 'submit' : 'button';
      this.button.dataset.choose = String(!selected);
      this.quantity.max = Number.isFinite(remaining) ? String(Math.max(1, remaining)) : '';
      this.quantity.readOnly = false;
      this.button.textContent = !selected ? this.dataset.choiceLabel || text(this, 'choose') : text(this, this.submitting ? 'adding' : 'add');
      this.retry.hidden = state.busy || !!state.readPromise || (!!state.cart && !state.uncertain);
      if (selected && !blocked && selected.available && remaining === 0 && !this.message.textContent) this.message.textContent = text(this, this.purchaseRemaining(selected) === 0 ? 'limit_reached' : 'unavailable');
    }
    async recheck() {
      if (state.busy || state.readPromise) return;
      state.busy = true;
      const wasUncertain = state.uncertain;
      this.message.textContent = text(this, 'checking');
      refreshAll();
      try {
        const cart = await fetchCart(this.config);
        this.message.textContent = wasUncertain ? text(this, 'updated') : '';
        if (wasUncertain) this.showNotice(this.message.textContent, cart);
      }
      catch { this.message.textContent = text(this, state.uncertain ? 'uncertain' : 'check_failed'); }
      finally { state.busy = false; refreshAll(); }
    }
    showNotice(message, cart) {
      if (!this.notice) return;
      this.notice.querySelector('[data-bl-quick-add-status]').textContent = message;
      this.notice.querySelector('[data-bl-quick-add-count]').textContent = text(this, 'cart_count', { count: cart.item_count });
      this.notice.hidden = false;
      const proceed = this.notice.querySelector('[data-bl-quick-add-continue]');
      proceed.onclick = () => {
        this.notice.hidden = true;
        if (!this.quantity.disabled) this.quantity.focus();
        else this.closest('li')?.querySelector('.bl-card')?.focus();
      };
      proceed.focus({ preventScroll: true });
    }
    async submit(event) {
      event.preventDefault();
      if (state.busy || state.readPromise || this.submitting) return;
      const selection = this.selection();
      const requested = integer(this.quantity.value);
      if (!selection) { this.message.textContent = text(this, 'choose_error'); this.select.focus(); return; }
      if (!selection.available) { this.message.textContent = text(this, 'unavailable'); return; }
      if (requested === null || requested < 1) { this.message.textContent = text(this, 'quantity_error'); return; }
      if (state.uncertain) { this.message.textContent = text(this, 'uncertain'); return; }
      state.busy = true;
      this.submitting = true;
      this.message.textContent = '';
      if (this.notice) this.notice.hidden = true;
      refreshAll();
      let attempted = false;
      try {
        let before;
        try { before = await fetchCart(this.config); }
        catch { this.message.textContent = text(this, 'check_failed'); state.cart = null; return; }
        const remaining = this.remaining(selection, before);
        if (requested > remaining) { this.message.textContent = text(this, remaining ? 'limit' : this.purchaseRemaining(selection, before) === 0 ? 'limit_reached' : 'unavailable', { count: remaining }); return; }
        const oldQuantity = sum(before, selection.id, 'variant');
        let responseOk = false;
        let responseKnown = false;
        let responseError = '';
        attempted = true;
        try {
          const { response, result } = await request(`${this.config.addUrl}.js`, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: selection.id, quantity: requested }] })
          });
          responseOk = response.ok;
          responseKnown = response.ok || (response.status >= 400 && response.status < 500);
          if (!response.ok) responseError = typeof result.description === 'string' ? result.description : '';
        } catch { responseOk = false; }
        let after;
        try { after = await fetchCart(this.config); }
        catch { state.uncertain = true; this.message.textContent = text(this, 'uncertain'); return; }
        const difference = sum(after, selection.id, 'variant') - oldQuantity;
        if (difference > 0) {
          let message = text(this, difference > requested ? 'updated' : difference === requested && responseOk ? 'added' : 'partial', { count: difference, requested });
          if (responseError) message += ` ${responseError}`;
          this.message.textContent = message;
          this.quantity.value = '1';
          this.showNotice(message, after);
        } else {
          const uncertain = !responseKnown || responseOk;
          if (uncertain) state.uncertain = true;
          this.message.textContent = uncertain ? text(this, 'uncertain') : responseError || text(this, 'not_added');
        }
      } catch {
        if (attempted) state.uncertain = true;
        this.message.textContent = text(this, attempted ? 'uncertain' : 'check_failed');
      } finally {
        this.submitting = false;
        state.busy = false;
        refreshAll();
      }
    }
  }
  customElements.define('bl-quick-add', QuickAdd);
  window.addEventListener('pageshow', event => {
    if (!event.persisted || state.busy || !state.components.size) return;
    const component = state.components.values().next().value;
    state.cart = null;
    refreshAll();
    initialCart(component.config).then(refreshAll).catch(() => {
      state.components.forEach(item => { item.message.textContent = text(item, 'check_failed'); });
      refreshAll();
    });
  });
})();
